import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormik } from 'formik';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  isRestaurantWizardStep,
  RESTAURANT_WIZARD_STEP_TITLES,
  RestaurantWizardStep,
  RestaurantWizardSteps
} from '../../../constants/RestaurantWizardConstants';
import { useRestaurantContext } from '../../../contexts/RestaurantContext';
import { PAGE_CREATE_RESTAURANT } from '../../../constants/UriConstants';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';
import { useMenuLayoutContext } from '../../../contexts/MenuLayoutContext';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';
import { RestaurantWizardSchema, RestaurantWizardFormValues } from './restaurantWizardSchema';
import useStripeSession from './useStripeSession';
import useRestaurantImages from './useRestaurantImages';
import useRestaurantWizardInitialValues from './useRestaurantWizardInitialValues';
import useRestaurantWizardSubmit from './useRestaurantWizardSubmit';
import RestaurantWizardHeader from './RestaurantWizardHeader';
import { getNextStep, getPreviousStep, getWizardStepPath } from './restaurantWizardNavigation';
import RestaurantBasicInfoPage from './RestaurantBasicInfoPage';
import RestaurantLocationPage from './RestaurantLocationPage';
import RestaurantHoursPage from './RestaurantHoursPage';
import RestaurantLogoPhotoPage from './RestaurantLogoPhotoPage';
import RestaurantPhotoPage from './RestaurantPhotoPage';
import RestaurantLayoutPage from './RestaurantLayoutPage';
import RestaurantSocialsPage from './RestaurantSocialsPage';
import RestaurantConfirmationPage from './RestaurantConfirmationPage';

export { AddressSchema, RestaurantSocialsSchema } from './restaurantWizardSchema';

const RestaurantWizardContent = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { step } = useParams<{ step: string }>();
  const { isLoading, currentRestaurant } = useRestaurantContext();
  const {
    permissions: { hasPairings, hasImageUpload }
  } = usePermissionsContext();
  const isSuper = hasPairings && hasImageUpload;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isEdit = !pathname.includes(PAGE_CREATE_RESTAURANT);
  const currentStep: RestaurantWizardStep = isRestaurantWizardStep(step) ? step : RestaurantWizardSteps.BASIC;

  const goToStep = useCallback(
    (targetStep: RestaurantWizardStep) => {
      navigate(getWizardStepPath(isEdit, targetStep));
    },
    [navigate, isEdit]
  );

  const goToNextStep = useCallback(() => {
    const nextStep = getNextStep(currentStep);
    if (nextStep) {
      goToStep(nextStep);
    }
  }, [currentStep, goToStep]);

  const goToPreviousStep = useCallback(() => {
    const previousStep = getPreviousStep(currentStep);
    if (previousStep) {
      goToStep(previousStep);
    }
  }, [currentStep, goToStep]);

  useEffect(() => {
    if (!isRestaurantWizardStep(step)) {
      navigate(getWizardStepPath(isEdit, RestaurantWizardSteps.BASIC), { replace: true });
    }
  }, [step, navigate, isEdit]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  const { loadLayouts } = useMenuLayoutContext();
  const { loadCuisines } = useCuisinesContext();
  useEffect(() => {
    loadLayouts();
    loadCuisines();
  }, [loadLayouts, loadCuisines]);

  const {
    restaurantImages,
    handleLogoImageUpload,
    handleThumbnailImageUpload,
    handleProfileImagesUpload,
    handleMenuCoverImageUpload,
    handleGalleryImagesUpload
  } = useRestaurantImages(isEdit, currentRestaurant);

  useStripeSession(isEdit, isSuper);

  const handleOnSubmit = useRestaurantWizardSubmit(isEdit, isSuper, restaurantImages, goToStep);
  const initialValues = useRestaurantWizardInitialValues(isEdit, currentRestaurant);

  const formik = useFormik<RestaurantWizardFormValues>({
    initialValues,
    validationSchema: RestaurantWizardSchema,
    enableReinitialize: false,
    onSubmit: handleOnSubmit,
    validateOnBlur: true
  });

  const wizardPage = useMemo(() => {
    if (isEdit && !currentRestaurant?.restaurantID) {
      return null;
    }

    switch (currentStep) {
      case RestaurantWizardSteps.LOCATION:
        return <RestaurantLocationPage formik={formik} onNext={goToNextStep} onBack={goToPreviousStep} />;
      case RestaurantWizardSteps.HOURS:
        return <RestaurantHoursPage formik={formik} onNext={goToNextStep} onBack={goToPreviousStep} />;
      case RestaurantWizardSteps.LOGO:
        return (
          <RestaurantLogoPhotoPage
            logoImage={restaurantImages.logoImage}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            handleLogoImageUpload={handleLogoImageUpload}
          />
        );
      case RestaurantWizardSteps.PHOTOS:
        return (
          <RestaurantPhotoPage
            thumbnailImage={restaurantImages.thumbnailImage}
            profileImages={restaurantImages.profileImages ?? []}
            menuCoverImage={restaurantImages.menuCoverImage}
            galleryImages={restaurantImages.galleryImages ?? []}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            handleThumbnailImageUpload={handleThumbnailImageUpload}
            handleProfileImagesUpload={handleProfileImagesUpload}
            handleMenuCoverImageUpload={handleMenuCoverImageUpload}
            handleGalleryImagesUpload={handleGalleryImagesUpload}
          />
        );
      case RestaurantWizardSteps.LAYOUT:
        return <RestaurantLayoutPage formik={formik} onNext={goToNextStep} onBack={goToPreviousStep} />;
      case RestaurantWizardSteps.SOCIAL:
        return (
          <RestaurantSocialsPage
            formik={formik}
            isEdit={isEdit}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            onSave={() => formik.submitForm()}
          />
        );
      case RestaurantWizardSteps.CONFIRM:
        return (
          <RestaurantConfirmationPage
            formik={formik}
            onBack={goToPreviousStep}
            restaurantImages={restaurantImages}
          />
        );
      case RestaurantWizardSteps.BASIC:
        return <RestaurantBasicInfoPage formik={formik} onNext={goToNextStep} />;
      default: {
        const _exhaustiveCheck: never = currentStep;
        return _exhaustiveCheck;
      }
    }
  }, [
    currentStep,
    currentRestaurant?.restaurantID,
    formik,
    isEdit,
    restaurantImages,
    goToNextStep,
    goToPreviousStep,
    handleLogoImageUpload,
    handleThumbnailImageUpload,
    handleProfileImagesUpload,
    handleMenuCoverImageUpload,
    handleGalleryImagesUpload
  ]);

  if (isEdit && !currentRestaurant?.restaurantID && !isLoading) {
    return (
      <div className="restaurant-wizard-container">
        <p className="restaurant-wizard-error">No restaurant found for this account.</p>
      </div>
    );
  }

  return (
    <div className="restaurant-wizard-container" ref={scrollContainerRef}>
      <div className="restaurant-wizard-hero-background" aria-hidden="true" />
      <div className="restaurant-wizard-scroll-body">
        <RestaurantWizardHeader title={RESTAURANT_WIZARD_STEP_TITLES[currentStep]} />
        {isLoading ? (
          <div className="restaurant-wizard-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <form className="restaurant-wizard-content" onSubmit={formik.handleSubmit}>
            {wizardPage}
          </form>
        )}
      </div>
    </div>
  );
};

const RestaurantWizard = () => (
  <Routes>
    <Route index element={<Navigate to={RestaurantWizardSteps.BASIC} replace />} />
    <Route path=":step" element={<RestaurantWizardContent />} />
  </Routes>
);

export default RestaurantWizard;
